import { RefreshRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNeedsReview, useRetryEntry } from "@/hooks/useAccounting";
import { formatDate } from "@/utils/date-format";

const money = (v: string) =>
  Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
};

/**
 * Entries held back from every export until their account mapping resolves.
 *
 * Its own tab rather than a filter on the blotter: a held entry is written, correct, and silently
 * excluded, so a month can be exported, balance, import, and be short with nothing saying so. The
 * only way that gets noticed is if somebody is shown a number that isn't zero.
 *
 * There is deliberately no edit. The entry's amounts and accounts are right — only the QuickBooks
 * number couldn't be matched, almost always because the account was renamed or made inactive. So
 * the fix belongs to the mapping, and Retry re-reads it. An edit here would let someone change
 * amounts to work around a configuration problem.
 */
export default function NeedsReviewPanel() {
  const { data: entries = [], isLoading } = useNeedsReview();
  const retry = useRetryEntry();
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onRetry = async (id: number, journalNo: string) => {
    setError(null);
    try {
      const r = await retry.mutateAsync(id);
      setNote(
        r.resolved
          ? `${journalNo} resolved — it will go out in the next batch.`
          : `${journalNo} still can't be matched: ${r.unresolved.join(", ")}`,
      );
    } catch (e) {
      setError(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Couldn't retry that entry.",
      );
    }
  };

  if (isLoading) return <Box sx={{ p: 2 }}>Loading…</Box>;

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {entries.length === 0 ? (
        <Alert severity="success">
          Nothing held for review. Every entry resolves to a QuickBooks account and will be included
          in its period's export.
        </Alert>
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {entries.length} {entries.length === 1 ? "entry is" : "entries are"} excluded from
              export
            </Typography>
            <Typography sx={{ fontSize: 12.5 }}>
              Each one is recorded in full and balances — but its QuickBooks account couldn't be
              matched, so it is left out of every batch. Correct the account mapping, then Retry.
              The entry itself is never edited: its amounts and accounts are already right.
            </Typography>
          </Alert>

          <Box
            sx={{
              border: "1px solid var(--color-card-border)",
              borderRadius: "var(--radius-card)",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>Journal No</TableCell>
                  <TableCell sx={th}>Date</TableCell>
                  <TableCell sx={th}>Event</TableCell>
                  <TableCell sx={th}>Client</TableCell>
                  <TableCell sx={th}>Unmatched account</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Amount</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>
                      {e.journalNo}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{formatDate(e.accountingDate)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {e.reasonLabel}
                      {e.memo && (
                        <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                          {e.memo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {e.organizationName ?? "—"}
                      {e.groupName && (
                        <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                          {e.groupName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.unresolvedAccounts.length === 0 ? (
                        // Held for a reason other than an account — currently only "no open period".
                        <Chip
                          size="small"
                          label="No open period"
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        e.unresolvedAccounts.map((a) => (
                          <Chip
                            key={a}
                            size="small"
                            label={a}
                            color="error"
                            variant="outlined"
                            sx={{ mr: 0.5, fontFamily: "monospace", fontSize: 11 }}
                          />
                        ))
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(e.totalDebit)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "right" }}>
                      <Button
                        size="small"
                        startIcon={
                          retry.isPending ? (
                            <CircularProgress aria-label="Loading" size={14} />
                          ) : (
                            <RefreshRounded />
                          )
                        }
                        disabled={retry.isPending}
                        onClick={() => onRetry(e.id, e.journalNo)}
                        sx={{ textTransform: "none" }}
                      >
                        Retry
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      <Snackbar
        open={note != null}
        autoHideDuration={5000}
        onClose={() => setNote(null)}
        message={note ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
